import { usePermissions } from 'hooks/usePermissions';
import React, { useContext, useEffect, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import * as store from 'store';
import { OrderTypes, SpotlightPermission } from 'types/enums';
import { safeJSONParse } from 'utils/utils';
import { getOrders } from '../api';
import { DateRange, Order } from '../types';

interface OrdersContextType {
  orderType: number;
  setOrderType: (orderType: number) => void;

  startDate: string;
  setStartDate: (startDate: string) => void;

  endDate: string;
  setEndDate: (endDate: string) => void;

  orderID: string | null;
  setOrderID: (orderID: string | null) => void;

  selectedStores: string[];
  setSelectedStores: (selectedStores: string[]) => void;

  selectedStatus: number[];
  setSelectedStatus: (selectedStatus: number[]) => void;

  userOrderTypeID: number;

  orderTypes: { id: number; name: string }[];

  orders: Order[];
  //setOrders: (selectedStatus: Order[]) => void;

  result: { [arg: string]: any };
}

const OrdersContext = React.createContext<OrdersContextType>({
  orderTypes: [],

  orderType: 0,
  setOrderType: (orderType) => orderType,

  startDate: new Date().toISOString().slice(0, 10),
  setStartDate: (startDate) => startDate,

  endDate: new Date().toISOString().slice(0, 10),
  setEndDate: (endDate) => endDate,

  orderID: null,
  setOrderID: (orderID) => orderID,

  selectedStores: [],
  setSelectedStores: (selectedStores) => selectedStores,

  selectedStatus: [],
  setSelectedStatus: (selectedStatus) => selectedStatus,

  userOrderTypeID: 0,

  orders: [],
  //setOrders: (orders) => orders,

  result: {},
});

export const useOrdersContext = () => useContext(OrdersContext);

export const OrdersProvider = ({ children }: { children: React.ReactNode }) => {
  const canViewAllOrderTypes = usePermissions().isPermitted([SpotlightPermission.SEE_ALL]);

  const accessToken = store.get('spotlightAccessToken');

  const today = new Date().toISOString().slice(0, 10);

  const defaultRange: DateRange = { startDate: today, endDate: today };

  const storedDateRange = (() => {
    const raw = store.get('dateRange');
    const parsed = safeJSONParse<DateRange>(raw, defaultRange);

    const startDate = parsed.startDate || today;
    const endDate = parsed.endDate || today;

    if (!parsed.startDate || !parsed.endDate) {
      store.set('dateRange', JSON.stringify({ startDate, endDate }));
    }

    return { startDate, endDate };
  })();

  const [startDate, setStartDate] = useState(() => storedDateRange.startDate);
  const [endDate, setEndDate] = useState(() => storedDateRange.endDate);

  const [selectedStores, setSelectedStores] = useState<string[] | []>([]);
  const [selectedStatus, setSelectedStatus] = useState<number[] | []>([]);
  const [orderID, setOrderID] = useState<string | null>(null);
  const [orderType, setOrderType] = useState(0);

  let orders: Order[] = [];

  const {
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    data,
    ...result
  } = useInfiniteQuery(
    ['orders', endDate, startDate, orderID, accessToken],
    ({ pageParam = 1 }) => getOrders({ startDate, endDate, orderID, accessToken, perPage: 10000, page: pageParam }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.nextPage) return undefined;
        // setNextCursor(lastPage.nextCursor)
        return lastPage.nextPage;
      },
    }
  );

  if (data && data.pages && data.pages.length) {
    orders = data.pages.flatMap((page, i) => {
      if (page.hasMore && hasNextPage) fetchNextPage();
      return page.data;
    });
  }

  const userOrderTypeID = orders[0]?.agentOrderTypeID ?? OrderTypes.ALL;

  const orderTypes = Object.keys(OrderTypes)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      id: OrderTypes[key as keyof typeof OrderTypes],
      name: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
    }))
    .filter((o) => (canViewAllOrderTypes ? o : [userOrderTypeID].includes(o.id)));

  useEffect(() => {
    if (!canViewAllOrderTypes && orderTypes.length === 1) setOrderType(orderTypes[0].id);
  }, [canViewAllOrderTypes, orderTypes]);

  return (
    <OrdersContext.Provider
      value={{
        orderType,
        setOrderType: (orderType) => setOrderType(orderType),

        startDate,
        setStartDate: (startDate) => setStartDate(startDate),

        endDate,
        setEndDate: (endDate) => setEndDate(endDate),

        orderID,
        setOrderID: (orderID) => setOrderID(orderID),

        selectedStores,
        setSelectedStores: (selectedStores) => setSelectedStores(selectedStores),

        selectedStatus,
        setSelectedStatus: (selectedStatus) => setSelectedStatus(selectedStatus),

        userOrderTypeID,
        orderTypes,
        orders,
        result,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};
